// Helper function to add delay
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

// Helper function to add timeout to promises
export const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string = "Operation timed out",
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs),
    ),
  ]);
};

// Retry configuration interface
interface RetryConfig {
  maxAttempts?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
}

// Helper function to retry operations with exponential backoff
export const withRetry = async <T>(
  operation: () => Promise<T>,
  config: RetryConfig = {},
): Promise<T> => {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
  } = config;

  let lastError: Error = new Error("Unknown error");

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry on user rejection or certain errors
      if (
        lastError.message.includes("user rejected") ||
        lastError.message.includes("User rejected") ||
        lastError.message.includes("rejected by user")
      ) {
        throw lastError;
      }

      // If this was the last attempt, throw the error
      if (attempt === maxAttempts) {
        throw lastError;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(
        baseDelay * Math.pow(backoffMultiplier, attempt - 1),
        maxDelay,
      );

      console.warn(
        `Operation failed (attempt ${attempt}/${maxAttempts}), retrying in ${delay}ms:`,
        lastError.message,
      );

      await sleep(delay);
    }
  }

  throw lastError;
};
